/**
 * Block B Phase 2.D (2026-06-01) — Migration Wizard UI.
 *
 * ч.1083 (рішення власника): сторінка з двох кроків —
 *   «Крок 1. Завантажте обробку для 1С» (ч.1436: користувач обирає платформу, форму і
 *     конфігурацію — і отримує готову обробку; вихідні тексти — лише в консолі платформи),
 *   «Крок 2. Майстер міграції» — етапи нижче.
 * Питання-відповіді колишньої сторінки — у довідці (KB faq/migration-faq.md).
 *
 * 6 етапів майстра для конфігурації міграції з 1С УТП:
 *   1. Дата міграції (з рекомендацією 31 грудня)
 *   2. Профіль (small/medium/enterprise/custom)
 *   3. Кастомізація глибин (тільки для custom)
 *   4. Підтвердження
 *   5. Iнструкція для 1С обробки (Run ID + параметри)
 *   6. Чекання на 1С (live status)
 *
 * Phase 1 Review Decisions (власник 2026-06-01) — інтегровано
 * через PROFILE_DEFAULTS у api/migrationRuns.ts.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SEVERITY_LABEL, sortFindings, summaryLine } from '@/pages/checkupModel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { orgs } from '@/api/catalogs';
import { getMe } from '@/api/auth';
import {
  migrationRuns,
  PROFILE_DEFAULTS,
  type MigrationDepthConfig,
  type MigrationProfile,
  type MigrationRun,
  type OsvReconciliation,
  type ReconciliationFinding,
  type ReconciliationReport,
  type ReconciliationSource,
  type CheckupReport,
} from '@/api/migrationRuns';
import {
  migration as migrationApi, oneCProcessing,
  type OneCConfig, type OneCForm, type OneCPlatform, type OneCWaitVariant,
} from '@/api/migration';
import DatePopover from '@/components/DatePopover';
import MigrationResetDialog from './MigrationResetDialog';
import { profileLabel, runDate, runStatusLabel } from '@/shared/utils/migrationRunLabels';
import DocBlock from '@/shared/components/DocBlock';
import { DOC_ROUTES } from '@/shared/utils/docRoutes';
import { docTableName } from '@/utils/moduleNames';

type Step = 1 | 2 | 3 | 4 | 5 | 6;

interface WizardState {
  step: Step;
  organization_id: string;
  migration_date: string;
  profile: MigrationProfile;
  depth_config: MigrationDepthConfig;
  run_id: string | null;
  /** Стан перенесення на момент, коли майстер його відкрив (ч.1139). */
  run_status: string | null;
}

const INITIAL_STATE: WizardState = {
  step: 1,
  organization_id: '',
  migration_date: '',
  profile: 'full_clone',
  depth_config: PROFILE_DEFAULTS.full_clone,
  run_id: null,
  run_status: null,
};

/** ч.1139: з якого етапу продовжувати перенесення залежно від його стану.
 *  Раніше «продовжити» завжди вело на етап 5, і «Я готовий, стартую» для вже
 *  запущеного перенесення відповідало 409 — до етапу 6 (прогрес, проведення)
 *  бухгалтер дійти не міг (бій, 2026-10-07). */
export function stepForRun(status: string): Step {
  if (status === 'WIZARD_IN_PROGRESS') return 4;
  if (status === 'READY') return 5;
  return 6;
}

export default function MigrationWizardPage() {
  const qc = useQueryClient();
  // ч.1126: тестовий акаунт (позначку ставить власник платформи) переносить
  // з 1С без підтвердження організації — сервер перевіряє те саме.
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const testerAccount = !!meQ.data?.tenant_is_tester;
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  // ч.1139: «продовжити в майстрі» з «Історії міграцій» відкриває саме це
  // перенесення на потрібному етапі (раніше посилання вело на порожній етап 1).
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get('run');
  const resumeRun = useQuery({
    queryKey: ['migration-run-resume', resumeId],
    queryFn: () => migrationRuns.get(resumeId as string),
    enabled: resumeId !== null && state.run_id === null,
  });
  function adoptRun(run: MigrationRun) {
    setState({
      step: stepForRun(run.status),
      organization_id: run.organization_id,
      migration_date: run.migration_date,
      profile: run.profile as MigrationProfile,
      depth_config: run.depth_config,
      run_id: run.id,
      run_status: run.status,
    });
  }
  useEffect(() => {
    if (resumeRun.data && state.run_id === null) adoptRun(resumeRun.data);
  }, [resumeRun.data, state.run_id]);

  // ч.1083: усі перенесення акаунта — для підказки «є незавершене» і для
  // того, чи розгортати «Крок 1» (обробку збирають один раз: хто вже
  // переносив, тому файли не потрібні щоразу).
  const runsList = useQuery({
    queryKey: ['migration-runs'],
    queryFn: () => migrationRuns.list(),
  });
  const [downloadOpen, setDownloadOpen] = useState<boolean | null>(null);
  const downloadRef = useRef<HTMLDivElement>(null);
  const hasRuns = (runsList.data ?? []).length > 0;
  const downloadExpanded = downloadOpen ?? (runsList.isSuccess && !hasRuns);
  function showDownload() {
    setDownloadOpen(true);
    downloadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const orgList = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgs.list({ limit: 200 }),
  });

  const createRun = useMutation({
    // ч.1137: перенесення вже є (скинуте або ми повернулись з етапу 5) —
    // зберігаємо його налаштування, а не створюємо друге (було б 409).
    mutationFn: () => {
      const settings = {
        migration_date: state.migration_date,
        profile: state.profile,
        depth_config:
          state.profile === 'custom' ? state.depth_config : undefined,
      };
      return state.run_id
        ? migrationRuns.update(state.run_id, settings)
        : migrationRuns.create({ organization_id: state.organization_id, ...settings });
    },
    onSuccess: (run) => {
      setState((s) => ({ ...s, run_id: run.id, run_status: 'WIZARD_IN_PROGRESS', step: 5 }));
      qc.invalidateQueries({ queryKey: ['migration-runs'] });
    },
  });

  // Block B 2.D fix (2026-06-01): обробка 409 — підвантажуємо існуючі runs
  // щоб користувач міг продовжити або скасувати existing run.
  const activeRunsForOrg = useQuery({
    queryKey: ['migration-runs-active', state.organization_id],
    queryFn: () =>
      state.organization_id
        ? migrationRuns.list({ organization_id: state.organization_id })
        : Promise.resolve([]),
    enabled: !!state.organization_id && createRun.isError,
  });

  const removeRun = useMutation({
    mutationFn: (id: string) => migrationRuns.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['migration-runs-active'] });
      createRun.reset();
    },
  });

  const continueRun = useMutation({
    // Повний стан перенесення (у списку немає глибин) → етап за його станом.
    mutationFn: (id: string) => migrationRuns.get(id),
    onSuccess: (run) => {
      adoptRun(run);
      createRun.reset();
    },
  });
  function continueExistingRun(runId: string) {
    continueRun.mutate(runId);
  }

  // ч.1137: скинути перенесення, що зупинилось на прийомі даних. Після скидання
  // майстер відкриває його налаштування (етап 4): їх можна змінити і запустити знову.
  // ч.1141: діалог з попередженням і (для власника) галочкою «прибрати перенесене».
  const [resetDialogRun, setResetDialogRun] = useState<string | null>(null);
  const [resetNotice, setResetNotice] = useState<string | null>(null);
  function onResetDone(run: MigrationRun) {
    setResetDialogRun(null);
    setState({
      step: 4,
      organization_id: run.organization_id,
      migration_date: run.migration_date,
      profile: run.profile as MigrationProfile,
      depth_config: run.depth_config,
      run_id: run.id,
      run_status: run.status,
    });
    setResetNotice(run.progress_message || 'Перенесення скинуто.');
    createRun.reset();
    qc.invalidateQueries({ queryKey: ['migration-runs'] });
    qc.invalidateQueries({ queryKey: ['migration-runs-active'] });
    qc.invalidateQueries({ queryKey: ['migration-run', run.id] });
  }
  function askResetRun(runId: string) {
    setResetDialogRun(runId);
  }

  const startRun = useMutation({
    mutationFn: (id: string) => migrationRuns.start(id),
    onSuccess: () => setState((s) => ({ ...s, run_status: 'READY', step: 6 })),
  });

  // Block B Phase 3: bulk-POST мутація
  const bulkPostRun = useMutation({
    mutationFn: (id: string) => migrationRuns.bulkPost(id),
    onSuccess: () => {
      // Live polling step 6 підхопить новий стан
      qc.invalidateQueries({ queryKey: ['migration-run', state.run_id] });
    },
  });

  const liveRun = useQuery({
    queryKey: ['migration-run', state.run_id],
    queryFn: () =>
      state.run_id ? migrationRuns.get(state.run_id) : Promise.resolve(null),
    enabled: state.step === 6 && state.run_id !== null,
    refetchInterval: state.step === 6 ? 3000 : false,
  });

  // ч.1342: проведення після прийому пакета запускає СЕРВЕР (раніше — лише ця
  // сторінка, поки відкрита: закрили вкладку — перенесення висіло на 50 %). Тут
  // лишається кнопка «запустити проведення вручну» для повтору.

  function setProfile(p: MigrationProfile) {
    if (p === 'custom') {
      setState((s) => ({ ...s, profile: p }));
    } else {
      setState((s) => ({
        ...s,
        profile: p,
        depth_config: PROFILE_DEFAULTS[p],
      }));
    }
  }

  function nextStep() {
    setState((s) => ({ ...s, step: ((s.step + 1) as Step) }));
  }

  function prevStep() {
    setState((s) => ({ ...s, step: ((s.step - 1) as Step) }));
  }

  function reset() {
    setState(INITIAL_STATE);
    setResetNotice(null);
  }

  return (
    <div style={{ maxWidth: 900, margin: '1rem auto', padding: '1rem',
      display: 'grid', gap: '1rem' }}>
      <div>
        <h2 style={{ marginBottom: '0.35rem' }}>Майстер міграції з 1С</h2>
      </div>

      {state.step === 1 && <RunsNote runs={runsList.data ?? []} />}

      <div ref={downloadRef}>
        <DocBlock
          index={1}
          title="Завантажте обробку для 1С"
          hint="потрібна один раз"
          actions={(
            <button type="button" className="btn-link"
              onClick={() => setDownloadOpen(!downloadExpanded)}>
              {downloadExpanded ? 'згорнути' : 'розгорнути'}
            </button>
          )}
        >
          {downloadExpanded ? <DownloadProcessing /> : (
            <p style={{ margin: '0 0 0.5rem', color: 'var(--muted)', fontSize: '0.9em' }}>
              Готова обробка для вашої 1С — розгорніть, якщо обробки у вас ще
              немає або вона застаріла.
            </p>
          )}
        </DocBlock>
      </div>

      <DocBlock index={2} title="Майстер міграції"
        hint="налаштування і запуск перенесення">
        <ProgressBar step={state.step} />

        {state.step === 1 && (
          <Step1Date
            value={state.migration_date}
            onChange={(d) => setState((s) => ({ ...s, migration_date: d }))}
            orgId={state.organization_id}
            onOrgChange={(id) =>
              setState((s) => ({
                ...s, organization_id: id,
                run_id: id === s.organization_id ? s.run_id : null,
                run_status: id === s.organization_id ? s.run_status : null,
              }))
            }
            orgList={(orgList.data?.items ?? []).filter(
              (o) => (testerAccount ? !o.deletion_mark : !!o.kep_confirmed_at))}
            hiddenUnconfirmed={testerAccount ? 0
              : (orgList.data?.items ?? []).filter((o) => !o.kep_confirmed_at && !o.deletion_mark).length}
            testerAccount={testerAccount}
            onNext={nextStep}
          />
        )}

        {state.step === 2 && (
          <Step2Profile
            profile={state.profile}
            onProfileChange={setProfile}
            onNext={nextStep}
            onBack={prevStep}
          />
        )}

        {state.step === 3 && (
          <Step3CustomDepth
            config={state.depth_config}
            onChange={(c) => setState((s) => ({ ...s, depth_config: c }))}
            onNext={nextStep}
            onBack={prevStep}
            profile={state.profile}
          />
        )}

        {state.step === 4 && (
          <Step4Confirm
            state={state}
            orgName={
              orgList.data?.items.find((o) => o.id === state.organization_id)
                ?.name ?? '—'
            }
            onConfirm={() => createRun.mutate()}
            onBack={prevStep}
            isPending={createRun.isPending}
            error={createRun.error}
            activeRuns={activeRunsForOrg.data ?? []}
            onContinueExisting={continueExistingRun}
            onRemoveRun={(id) => removeRun.mutate(id)}
            removeRunPending={removeRun.isPending}
            existingRun={state.run_id !== null}
            resetNotice={resetNotice}
            onResetRun={askResetRun}
          />
        )}

        {state.step === 5 && state.run_id && (
          <Step5Instructions
            runId={state.run_id}
            migrationDate={state.migration_date}
            organizationId={state.organization_id}
            depthConfig={state.depth_config}
            onStart={() => state.run_id && startRun.mutate(state.run_id)}
            onBack={prevStep}
            isPending={startRun.isPending}
            onShowDownload={showDownload}
            alreadyStarted={state.run_status !== null && state.run_status !== 'WIZARD_IN_PROGRESS'}
            startError={startRun.isError ? extractErrorDetail(startRun.error).message : null}
            onGoProgress={() => setState((s) => ({ ...s, step: 6 }))}
          />
        )}

        {state.step === 6 && (
          <Step6Waiting
            run={liveRun.data ?? null}
            onReset={reset}
            onBulkPost={() => state.run_id && bulkPostRun.mutate(state.run_id)}
            bulkPostPending={bulkPostRun.isPending}
            onResetRun={askResetRun}
            onBackToKey={() => setState((s) => ({ ...s, step: 5 }))}
          />
        )}
      </DocBlock>
      {resetDialogRun && (
        <MigrationResetDialog
          runId={resetDialogRun}
          onClose={() => setResetDialogRun(null)}
          onDone={onResetDone}
        />
      )}
    </div>
  );
}

/**
 * ч.1083: підказка над майстром — про незавершене перенесення (продовжити
 * чи скасувати його майстер пропонує на етапі 4 для тієї ж організації) і
 * про повернення в 1С з «Історії міграцій» (30 днів після завершення).
 */
function RunsNote({ runs }: { runs: { status: string }[] }) {
  const active = runs.filter(
    (r) => !['COMPLETED', 'FAILED', 'ROLLED_BACK'].includes(r.status)).length;
  const completed = runs.filter((r) => r.status === 'COMPLETED').length;
  if (active === 0 && completed === 0) return null;
  const warn = active > 0;
  return (
    <div
      data-testid="migration-runs-note"
      style={{
        padding: '0.75rem 1rem', borderRadius: 8,
        background: warn ? 'var(--badge-amber-bg)' : 'var(--badge-green-bg)',
        border: `1px solid ${warn ? 'var(--badge-amber-bd)' : 'var(--badge-green-bd)'}`,
        color: 'var(--text)',
      }}
    >
      {warn ? (
        <>
          Є незавершене перенесення ({active}) — його видно в{' '}
          <Link to="/migration/runs">«Історії міграцій»</Link>. Щоб продовжити
          або скасувати його, пройдіть майстер для тієї ж організації: на етапі 4
          він запропонує вибір.
        </>
      ) : (
        <>
          Перенесення вже виконувалось. Повернути дані в 1С протягом 30 днів після
          завершення можна в <Link to="/migration/runs">«Історії міграцій»</Link>.
        </>
      )}
    </div>
  );
}

/** ч.1436 (рішення власника): з якої 1С переносять — три питання, потім посилання на обробку. */
const PLATFORM_OPTIONS: [OneCPlatform, string][] = [['8.2', '1С 8.2'], ['8.3', '1С 8.3'], ['bas', 'BAS']];
const FORM_OPTIONS: [OneCForm, string][] = [['managed', 'керована форма'], ['ordinary', 'звичайна форма']];
const CONFIG_OPTIONS: [OneCConfig, string][] = [
  ['utp12', 'УТП, редакція 1.2'], ['bp12', 'Бухгалтерія, редакція 1.2'], ['bp20', 'Бухгалтерія, редакція 2.0'],
];

function Choice<T extends string>({ label, hint, options, value, onChange }: {
  label: string; hint?: string; options: [T, string][]; value: T | null; onChange: (v: T) => void;
}) {
  return (
    <div style={{ marginBottom: '0.6rem' }}>
      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{label}</div>
      <div className="seg-tabs" role="group" aria-label={label}>
        {options.map(([v, text]) => (
          <button key={v} type="button" className={value === v ? 'active' : ''}
            aria-pressed={value === v} onClick={() => onChange(v)}>{text}</button>
        ))}
      </div>
      {hint && <div style={{ fontSize: '0.85em', color: 'var(--muted)', marginTop: '0.25rem' }}>{hint}</div>}
    </div>
  );
}

const fileSize = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024))} КБ`;

/** ч.1436: обробки ще немає — сказати одразу і дати записатися в перелік очікувачів. */
function InDevelopment({ variant }: { variant: OneCWaitVariant }) {
  const qc = useQueryClient();
  const waiting = useQuery({ queryKey: ['1c-processing-waitlist'], queryFn: oneCProcessing.waiting });
  const join = useMutation({
    mutationFn: () => oneCProcessing.notifyMe(variant),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['1c-processing-waitlist'] }),
  });
  const subscribed = waiting.data?.includes(variant) ?? false;
  return (
    <div data-testid="processing-in-development" className="mc__panel mc__panel--warn"
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
      <strong>Обробка в розробці</strong>
      {subscribed ? (
        <span>Повідомимо, щойно вона буде готова: у Telegram, якщо його підключено, інакше на пошту.</span>
      ) : (
        <button type="button" disabled={join.isPending || waiting.isLoading} onClick={() => join.mutate()}>
          {join.isPending ? 'записуємо…' : 'сповістити про готовність'}
        </button>
      )}
      {join.isError && <span role="alert">Не вдалося записати — спробуйте ще раз.</span>}
    </div>
  );
}

function DownloadProcessing() {
  const [platform, setPlatform] = useState<OneCPlatform | null>(null);
  const [form, setForm] = useState<OneCForm | null>(null);
  const [config, setConfig] = useState<OneCConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Варіант, для якого обробки ще немає, — з першого ж кроку, де його обрали.
  const inDevelopment: OneCWaitVariant | null = platform === 'bas' ? 'bas'
    : platform && form === 'managed' ? 'managed'
    : platform && form && config === 'bp20' ? 'bp20' : null;
  const choice = platform && form && config && !inDevelopment ? { platform, form, config } : null;
  const info = useQuery({
    queryKey: ['1c-processing', platform, form, config],
    queryFn: () => oneCProcessing.info(choice!),
    enabled: choice !== null,
  });
  const download = async (fileName: string) => {
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      await oneCProcessing.download(choice, fileName);
    } catch (e) {
      setError(e instanceof AxiosError && e.response?.status === 404
        ? 'Файл обробки зараз недоступний — напишіть у підтримку.'
        : 'Не вдалося завантажити обробку. Спробуйте ще раз.');
    } finally {
      setBusy(false);
    }
  };
  const ready = info.data?.available ? info.data : null;
  return (
    <div style={{ paddingBottom: '0.6rem' }}>
      <p style={{ marginTop: 0 }}>
        Оберіть, з якої програми переносите дані, — і завантажте готову обробку. Вона лише{' '}
        <strong>читає</strong> дані вашої 1С і нічого в ній не змінює.
      </p>
      <Choice label="1. Платформа 1С" options={PLATFORM_OPTIONS} value={platform} onChange={setPlatform} />
      {platform && platform !== 'bas' && (
        <Choice label="2. Форма" options={FORM_OPTIONS} value={form} onChange={setForm}
          hint="УТП і Бухгалтерія редакції 1.2 працюють у звичайній формі." />
      )}
      {platform && platform !== 'bas' && form && form !== 'managed' && (
        <Choice label="3. Конфігурація" options={CONFIG_OPTIONS} value={config} onChange={setConfig} />
      )}
      {inDevelopment && <InDevelopment variant={inDevelopment} />}
      {choice && info.isLoading && <p style={{ color: 'var(--muted)' }}>Шукаємо обробку…</p>}
      {choice && info.data && !info.data.available && (
        <div className="mc__panel mc__panel--warn" role="status">{info.data.reason}</div>
      )}
      {choice && info.isError && (
        <div className="mc__panel mc__panel--warn" role="alert">Не вдалося перевірити обробку. Оновіть сторінку.</div>
      )}
      {ready && (
        <div data-testid="processing-ready" style={{
          border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-soft)', padding: '0.75rem',
        }}>
          <button type="button" className="primary" disabled={busy}
            onClick={() => void download(ready.file_name)}>
            {busy ? 'завантаження…' : 'завантажити обробку'}
          </button>
          <span style={{ marginLeft: '0.6rem', fontSize: '0.85em', color: 'var(--muted)' }}>
            {ready.file_name} · {fileSize(ready.size)} · оновлено{' '}
            {new Date(ready.updated_at).toLocaleDateString('uk-UA')}
          </span>
          {error && <div role="alert" style={{ color: 'var(--badge-red-fg)', marginTop: '0.4rem' }}>{error}</div>}
          <ol style={{ margin: '0.6rem 0 0', paddingLeft: '1.2rem' }}>
            <li>Відкрийте файл у своїй 1С: <strong>Файл → Відкрити</strong>.</li>
            <li>Адресу і ключ для обробки дасть майстер на етапі 5.</li>
          </ol>
        </div>
      )}
    </div>
  );
}

function ProgressBar({ step }: { step: Step }) {
  const steps = [
    'Дата',
    'Профіль',
    'Глибини',
    'Підтвердження',
    'Iнструкція',
    'Прогрес',
  ];
  return (
    <div style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0' }}>
      {steps.map((title, i) => {
        const active = step === i + 1;
        const done = step > i + 1;
        return (
          <div
            key={i}
            style={{
              flex: 1,
              padding: '0.5rem',
              background: done ? 'var(--badge-green-bg)'
                : active ? 'var(--badge-amber-bg)' : 'var(--bg-soft)',
              color: 'var(--text)',
              border: active ? '2px solid var(--primary)' : '1px solid var(--border)',
              borderRadius: 4,
              textAlign: 'center',
              fontSize: '0.85em',
            }}
          >
            {i + 1}. {title}
          </div>
        );
      })}
    </div>
  );
}

interface Step1Props {
  value: string;
  onChange: (d: string) => void;
  orgId: string;
  onOrgChange: (id: string) => void;
  orgList: { id: string; name: string }[];
  /** ч.1110: скільки організацій сховано, бо не підтверджені (імпорт лише для підтверджених). */
  hiddenUnconfirmed?: number;
  /** ч.1126: тестовий акаунт — перенесення без підтвердження організації. */
  testerAccount?: boolean;
  onNext: () => void;
}

function Step1Date({ value, onChange, orgId, onOrgChange, orgList, hiddenUnconfirmed = 0, testerAccount = false, onNext }: Step1Props) {
  const today = new Date().toISOString().slice(0, 10);
  const canNext = orgId && value;
  return (
    <fieldset style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 6 }}>
      <legend>Етап 1: Організація і дата міграції</legend>

      <div className="field" style={{ marginBottom: '1rem' }}>
        <label>Організація *</label>
        <select value={orgId} onChange={(e) => onOrgChange(e.target.value)}>
          <option value="">— Оберіть організацію —</option>
          {orgList.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        {testerAccount && (
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.35rem' }}
            data-testid="wizard-tester-hint">
            Тестовий акаунт: перенесення з 1С доступне без підтвердження організації.
          </div>
        )}
        {hiddenUnconfirmed > 0 && (
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.35rem' }}
            data-testid="wizard-unconfirmed-hint">
            Перенесення з 1С доступне лише підтвердженим організаціям — {hiddenUnconfirmed} не показано.
            Підтвердіть організацію (підписання договору ключем КЕП) у довіднику{' '}
            <Link to="/organizations">Організації</Link>.
          </div>
        )}
      </div>

      <div className="field">
        <label>Дата міграції *</label>
        <DatePopover
          value={value}
          onChange={(v) => onChange(v)}
        />
        <div style={{ marginTop: '0.5rem' }}>
          <button type="button" onClick={() => onChange(today)}>
            Сьогодні (рекомендовано)
          </button>
        </div>
        <small style={{ color: 'var(--muted)', display: 'block', marginTop: '0.5rem' }}>
          Це дата ПЕРЕХОДУ на сервіс. Документи вигружаються за N місяців
          НАЗАД від цієї дати (глибина — крок 3). Початкові залишки форма 1С
          знімає АВТОМАТИЧНО — на кінець дня перед датою початку документів,
          окремо їх вводити не треба. Дата в минулому відсіче всі новіші
          документи.
        </small>
      </div>

      <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
        <button
          className="primary"
          disabled={!canNext}
          onClick={onNext}
        >
          Далі →
        </button>
      </div>
    </fieldset>
  );
}

interface Step2Props {
  profile: MigrationProfile;
  onProfileChange: (p: MigrationProfile) => void;
  onNext: () => void;
  onBack: () => void;
}

function Step2Profile({ profile, onProfileChange, onNext, onBack }: Step2Props) {
  return (
    <fieldset style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 6 }}>
      <legend>Етап 2: Модель перенесення</legend>

      <p style={{ color: 'var(--muted)' }}>
        Модель визначає, скільки історії переноситься. Старт завжди з{' '}
        <strong>1 січня</strong> — податкові накопичення зарплати обнуляються,
        а залишки звіряються з річним балансом. Усі види даних переносяться
        з однієї дати.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <ProfileOption
          value="full_clone"
          current={profile}
          onChange={onProfileChange}
          title="Повний клон (рекомендовано)"
          description="Вся історія бази (до 10 років). Облік = точна копія 1С, звірка копійка в копійку, 1С більше не потрібна. Проводки копіюються з 1С — розбіжності не накопичуються."
        />
        <ProfileOption
          value="deep_history"
          current={profile}
          onChange={onProfileChange}
          title="Глибока історія — 5 років (з 1 січня)"
          description="Для обліку ОЗ/амортизації, виробництва, старих партій. Архів 1С майже не потрібен; невеликий ризик на межі вікна."
        />
        <ProfileOption
          value="working_year"
          current={profile}
          onChange={onProfileChange}
          title="Робочий рік — з 1 січня минулого року"
          description="Мінімум, за якого зарплата (середні для відпусток/лікарняних) рахується без ручного вводу. За старішим — в архів 1С."
        />
        <ProfileOption
          value="clean_start"
          current={profile}
          onChange={onProfileChange}
          title="Чистий старт — лише залишки"
          description="Залишки на найближче 1 січня, історія не переноситься. Для нового/маленького бізнесу без найманих. Середні для відпусток — вручну."
        />
        <ProfileOption
          value="custom"
          current={profile}
          onChange={onProfileChange}
          title="Власні налаштування (експертний режим)"
          description="Точний контроль глибини для кожного виду даних окремо (місяцями)."
        />
      </div>

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack}>← Назад</button>
        <button className="primary" onClick={onNext}>
          Далі →
        </button>
      </div>
    </fieldset>
  );
}

interface ProfileOptionProps {
  value: MigrationProfile;
  current: MigrationProfile;
  onChange: (p: MigrationProfile) => void;
  title: string;
  description: string;
}

function ProfileOption({ value, current, onChange, title, description }: ProfileOptionProps) {
  const selected = current === value;
  return (
    <label
      style={{
        padding: '0.75rem',
        border: selected ? '2px solid var(--primary)' : '1px solid var(--border)',
        borderRadius: 6,
        background: selected ? 'var(--primary-soft)' : 'var(--bg-panel)',
        cursor: 'pointer',
      }}
    >
      <input
        type="radio"
        name="profile"
        value={value}
        checked={selected}
        onChange={() => onChange(value)}
        style={{ marginRight: '0.5rem' }}
      />
      <strong>{title}</strong>
      <div style={{ fontSize: '0.85em', color: 'var(--muted)', marginLeft: '1.5rem' }}>
        {description}
      </div>
    </label>
  );
}

interface Step3Props {
  config: MigrationDepthConfig;
  onChange: (c: MigrationDepthConfig) => void;
  onNext: () => void;
  onBack: () => void;
  profile: MigrationProfile;
}

function Step3CustomDepth({ config, onChange, onNext, onBack, profile }: Step3Props) {
  const isCustom = profile === 'custom';
  if (!isCustom) {
    // Не custom — показуємо preview і кнопку «далі»
    return (
      <fieldset style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 6 }}>
        <legend>Етап 3: Глибина історії</legend>
        <p>Для обраного профілю використовуються такі глибини:</p>
        <DepthPreview config={config} />
        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={onBack}>← Назад</button>
          <button className="primary" onClick={onNext}>
            Далі →
          </button>
        </div>
      </fieldset>
    );
  }

  return (
    <fieldset style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 6 }}>
      <legend>Етап 3: Власна глибина історії</legend>
      <p style={{ color: 'var(--muted)' }}>
        Кожне поле — кількість місяців історії документів. 0 = тільки залишки.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
        <DepthSlider
          label="Зарплата (місяців)"
          value={config.payroll_months}
          onChange={(v) => onChange({ ...config, payroll_months: v })}
          max={120}
          help="≥12м потрібно для жовтневих відпусток"
        />
        <DepthSlider
          label="Товари FIFO (місяців)"
          value={config.fifo_months}
          onChange={(v) => onChange({ ...config, fifo_months: v })}
          max={120}
          help="≥24м для типової торгівлі, ≥60м для виробництва"
        />
        <DepthSlider
          label="Касові/банк. виписки"
          value={config.cash_bank_months}
          onChange={(v) => onChange({ ...config, cash_bank_months: v })}
          max={120}
          help="12м — стандарт"
        />
        <DepthSlider
          label="Податкові накладні"
          value={config.tax_invoices_months}
          onChange={(v) => onChange({ ...config, tax_invoices_months: v })}
          max={120}
          help="24м для аудит-готовності"
        />
        <DepthSlider
          label="ПДВ пропорційне"
          value={config.vat_months}
          onChange={(v) => onChange({ ...config, vat_months: v })}
          max={120}
          help="0 = декларації у 1С; 12м для пропорційного ПДВ ст.199"
        />
        <DepthSlider
          label="Авансові звіти"
          value={config.advance_reports_months}
          onChange={(v) => onChange({ ...config, advance_reports_months: v })}
          max={120}
          help="12м потрібно для ФОП ЗСО річного звіту"
        />
        <DepthSlider
          label="Кадрові накази"
          value={config.hr_orders_months}
          onChange={(v) => onChange({ ...config, hr_orders_months: v })}
          max={120}
          help="0 = тільки активні працівники; >0 = архів наказів (накази, ЦПУ)"
        />
        <DepthSlider
          label="Аудит ОЗ-операцій"
          value={config.assets_audit_months}
          onChange={(v) => onChange({ ...config, assets_audit_months: v })}
          max={120}
          help="Snapshot карток + N місяців документних операцій"
        />
      </div>

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack}>← Назад</button>
        <button className="primary" onClick={onNext}>
          Далі →
        </button>
      </div>
    </fieldset>
  );
}

interface DepthSliderProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max: number;
  help: string;
}

function DepthSlider({ label, value, onChange, max, help }: DepthSliderProps) {
  return (
    <div className="field">
      <label>
        {label}: <strong>{value}м</strong>
      </label>
      <input
        type="range"
        min={0}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '100%' }}
      />
      <small style={{ color: 'var(--muted)' }}>{help}</small>
    </div>
  );
}

function DepthPreview({ config }: { config: MigrationDepthConfig }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        <DepthRow label="Зарплата" value={`${config.payroll_months} місяців`} />
        <DepthRow label="Товари FIFO" value={`${config.fifo_months} місяців`} />
        <DepthRow label="Касові/банк. виписки" value={`${config.cash_bank_months} місяців`} />
        <DepthRow label="Податкові накладні" value={`${config.tax_invoices_months} місяців`} />
        <DepthRow label="ПДВ пропорційне" value={`${config.vat_months} місяців`} />
        <DepthRow label="Авансові звіти" value={`${config.advance_reports_months} місяців`} />
        <DepthRow
          label="Основні засоби"
          value={`Snapshot ${config.assets_snapshot_only ? '✓' : '✗'} + ${config.assets_audit_months}м документів аудиту`}
        />
        <DepthRow
          label="Замовлення"
          value={config.orders_active_only ? 'Тільки активні' : 'Усі за період'}
        />
      </tbody>
    </table>
  );
}

function DepthRow({ label, value }: { label: string; value: string }) {
  return (
    <tr style={{ borderBottom: '1px solid #eee' }}>
      <td style={{ padding: '0.4rem 0', color: 'var(--muted)' }}>{label}</td>
      <td style={{ padding: '0.4rem 0', textAlign: 'right' }}>
        <strong>{value}</strong>
      </td>
    </tr>
  );
}

interface ActiveRunListItem {
  id: string;
  organization_id: string;
  migration_date: string;
  profile: string;
  status: string;
  progress_percent: number;
  created_at: string;
  completed_at: string | null;
}

interface Step4Props {
  state: WizardState;
  orgName: string;
  onConfirm: () => void;
  onBack: () => void;
  isPending: boolean;
  error: unknown;
  activeRuns: ActiveRunListItem[];
  onContinueExisting: (id: string) => void;
  onRemoveRun: (id: string) => void;
  removeRunPending: boolean;
  /** ч.1137: перенесення вже створено — етап зберігає його налаштування. */
  existingRun: boolean;
  /** ч.1141: що відповів сервер після скидання (дані лишились / прибрано). */
  resetNotice: string | null;
  onResetRun: (id: string) => void;
}

export { RESET_WARNING } from './MigrationResetDialog';

function extractErrorDetail(error: unknown): { code: number | null; message: string } {
  if (error instanceof AxiosError) {
    const code = error.response?.status ?? null;
    const data = error.response?.data;
    let message: string;
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail: unknown }).detail;
      message = typeof detail === 'string' ? detail : JSON.stringify(detail);
    } else {
      message = error.message;
    }
    return { code, message };
  }
  return { code: null, message: String(error) };
}

function Step4Confirm({
  state, orgName, onConfirm, onBack, isPending, error,
  activeRuns, onContinueExisting, onRemoveRun, removeRunPending,
  existingRun, resetNotice, onResetRun,
}: Step4Props) {
  const errorInfo = error !== null && error !== undefined ? extractErrorDetail(error) : null;
  const is409Conflict = errorInfo?.code === 409;
  // При 409 — фільтруємо тільки активні runs для поточної org
  const conflictingRuns = is409Conflict
    ? activeRuns.filter(
        (r) =>
          r.organization_id === state.organization_id &&
          !['COMPLETED', 'FAILED', 'ROLLED_BACK'].includes(r.status),
      )
    : [];

  return (
    <fieldset style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 6 }}>
      <legend>Етап 4: Підтвердження</legend>

      {resetNotice && (
        <div
          data-testid="wizard-reset-notice"
          style={{
            marginBottom: '1rem', padding: '0.75rem', borderRadius: 6,
            background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)',
          }}
        >
          <strong>{resetNotice}</strong>{' '}
          Щоб змінити модель, поверніться кнопкою «← Назад», потім збережіть і запустіть
          перенесення знову — в обробці 1С нічого міняти не треба.
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <DepthRow label="Організація" value={orgName} />
          <DepthRow label="Дата міграції" value={state.migration_date} />
          <DepthRow label="Модель перенесення" value={profileLabel(state.profile)} />
        </tbody>
      </table>

      <h4 style={{ marginTop: '1rem' }}>Глибини історії:</h4>
      <DepthPreview config={state.depth_config} />

      {is409Conflict && conflictingRuns.length > 0 && (
        <div
          style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: 'var(--badge-amber-bg)',
            border: '1px solid var(--badge-amber-bd)',
            borderRadius: 6,
          }}
        >
          <strong>Для цієї організації вже є незавершене перенесення.</strong>
          <p style={{ margin: '0.5rem 0' }}>
            Його можна <strong>продовжити</strong> або <strong>скасувати</strong>,
            щоб створити нове з іншими налаштуваннями.
          </p>
          {conflictingRuns.map((run) => (
            <div
              key={run.id}
              style={{
                marginTop: '0.75rem',
                padding: '0.5rem',
                background: 'var(--bg-panel)',
                borderRadius: 4,
                border: '1px solid #ddd',
              }}
            >
              <div data-testid="wizard-active-run"
                style={{ marginBottom: '0.5rem', fontSize: '0.9em' }}>
                <strong>Дата переходу:</strong> {runDate(run.migration_date)}
                {' · '}
                <strong>Модель:</strong> {profileLabel(run.profile)}
                {' · '}
                <strong>Стан:</strong> {runStatusLabel(run.status)}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className="primary"
                  onClick={() => onContinueExisting(run.id)}
                >
                  продовжити перенесення
                </button>
                {run.status === 'IMPORTING' ? (
                  // ч.1137: перенесення, що приймає дані, скасувати не можна —
                  // його скидають (повертають на налаштування), дані лишаються.
                  <button className="danger" onClick={() => onResetRun(run.id)}>
                    скинути перенесення
                  </button>
                ) : (
                  <button
                    className="danger"
                    onClick={() => {
                      if (
                        confirm(
                          'Скасувати перенесення? Його налаштування буде втрачено. '
                          + 'Дані з 1С за ним ще не приймались.',
                        )
                      ) {
                        onRemoveRun(run.id);
                      }
                    }}
                    disabled={removeRunPending}
                  >
                    {removeRunPending ? 'скасовуємо…' : 'скасувати перенесення'}
                  </button>
                )}
              </div>
              {run.status === 'IMPORTING' && (
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.85em', color: 'var(--muted)' }}>
                  Це перенесення зупинилось на прийомі даних. Продовжіть його — і повторіть
                  вивантаження в обробці 1С, або скиньте, щоб змінити налаштування.
                </p>
              )}

            </div>
          ))}
        </div>
      )}

      {errorInfo && !is409Conflict && (
        <div
          style={{
            color: 'var(--danger)',
            marginTop: '1rem',
            padding: '0.75rem',
            background: 'var(--badge-red-bg)',
            borderRadius: 6,
          }}
        >
          <strong>Помилка {errorInfo.code ?? '?'}:</strong> {errorInfo.message}
        </div>
      )}

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} disabled={isPending}>← Назад</button>
        <button
          className="primary"
          onClick={onConfirm}
          disabled={isPending || (is409Conflict && conflictingRuns.length > 0)}
        >
          {existingRun
            ? (isPending ? 'зберігаємо…' : 'зберегти та продовжити →')
            : (isPending ? 'Створюємо…' : 'Створити та продовжити →')}
        </button>
      </div>
    </fieldset>
  );
}

interface Step5Props {
  runId: string;
  migrationDate: string;
  organizationId: string;
  depthConfig: MigrationDepthConfig;
  onStart: () => void;
  onBack: () => void;
  isPending: boolean;
  /** ч.1083: розгорнути «Крок 1» (обробка для 1С) і прокрутити до нього. */
  onShowDownload: () => void;
  /** ч.1139: перенесення вже запущено — замість «стартую» ведемо до прогресу. */
  alreadyStarted: boolean;
  startError: string | null;
  onGoProgress: () => void;
}

/** ISO 'YYYY-MM-DD' → 'DD.MM.YYYY' для копіювання у 1С (укр. формат дати). */
function toUaDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/**
 * Обчислює «Дату початку документів» для обробки 1С: дата міграції мінус
 * НАЙБІЛЬША глибина (у місяцях) з усіх доменів. Обробка тягне документи
 * однією датою-початком, тож беремо max — щоб гарантовано попали і товарні
 * партії FIFO (найглибші), і решта. Повертає 'YYYY-MM-DD'.
 */
function computeDocStartDate(
  migrationDate: string,
  cfg: MigrationDepthConfig,
): { date: string; maxMonths: number; driver: string } {
  const domains: { months: number; label: string }[] = [
    { months: cfg.fifo_months, label: 'товарні партії (FIFO)' },
    { months: cfg.payroll_months, label: 'зарплата' },
    { months: cfg.tax_invoices_months, label: 'податкові накладні' },
    { months: cfg.goods_months, label: 'товарні документи' },
    { months: cfg.cash_bank_months, label: 'каса/банк' },
    { months: cfg.advance_reports_months, label: 'авансові звіти' },
    { months: cfg.commission_months, label: 'комісія' },
    { months: cfg.assets_audit_months, label: 'основні засоби' },
    { months: cfg.vat_months, label: 'ПДВ' },
    { months: cfg.hr_orders_months, label: 'кадрові накази' },
  ];
  const top = domains.reduce((a, b) => (b.months > a.months ? b : a));
  const d = new Date(`${migrationDate}T00:00:00`);
  if (Number.isNaN(d.getTime()) || top.months <= 0) {
    return { date: migrationDate, maxMonths: 0, driver: top.label };
  }
  // ВАЖЛИВО: спершу day=1, ПОТIМ -months. Інакше setMonth з днем 31
  // перекидає («31 лютого» → березень). Завжди перший день місяця.
  d.setDate(1);
  d.setMonth(d.getMonth() - top.months);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  return { date: iso, maxMonths: top.months, driver: top.label };
}

/**
 * Кнопка «копіювати» поряд зі значенням (Run ID / URL / дати у кроці 5).
 * navigator.clipboard з fallback на execCommand (старі браузери / http).
 * Показує «✓» 2 секунди після копіювання.
 */
function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // fallback для не-https / старих браузерів
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={label ? `Копіювати ${label}` : 'Копіювати'}
      style={{
        marginLeft: '0.4rem', padding: '0.05rem 0.4rem', fontSize: '0.8em',
        cursor: 'pointer', verticalAlign: 'middle',
      }}
    >
      {copied ? '✓' : '📋'}
    </button>
  );
}

function Step5Instructions({
  runId, migrationDate, organizationId, depthConfig, onStart, onBack, isPending,
  onShowDownload, alreadyStarted, startError, onGoProgress,
}: Step5Props) {
  const docStart = computeDocStartDate(migrationDate, depthConfig);
  // Адрес — з поточного origin (звідки відкрито сайт), НЕ хардкод oblik.plus:
  // dev1 → https://oblik.plus, другий розробник → http://localhost:8081 (свій backend
  // через SSH-тунель). Так імпорт іде у ТУ базу, з якої відкрито візард (Норма #28).
  const importUrl = `${window.location.origin}/api/v1/migration/import`;
  return (
    <fieldset style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 6 }}>
      <legend>Етап 5: Значення для обробки 1С/BAS</legend>

      <div style={{ background: 'var(--primary-soft)', padding: '1rem', borderRadius: 6, marginBottom: '1rem' }}>
        <strong>Конфігурація створена!</strong> Тепер потрібен{' '}
        <strong>міграційний токен</strong> — це ключ доступу для 1С/BAS
        обробки. Створіть його тут (можна повторно, токени накопичуються
        у списку):
      </div>

      <TokenCreator organizationId={organizationId} />

      <h3 style={{ marginTop: '1.5rem' }}>Кроки у вашій 1С/BAS:</h3>
      <ol>
        <li>Відкрийте у 1С обробку з кроку 1 цієї сторінки
          ({' '}<button type="button" className="btn-link" onClick={onShowDownload}>
            завантажити обробку
          </button>{' '}) через <strong>Файл → Відкрити</strong>
        </li>
        <li>У формі обробки введіть:
          <ul>
            <li><strong>URL</strong>: <code>{importUrl}</code>
              <CopyButton value={importUrl} label="URL" /></li>
            <li><strong>Migration Run ID</strong>: <code>{runId}</code>
              <CopyButton value={runId} label="Run ID" /></li>
            <li>
              <strong>Дата початку документів</strong>:{' '}
              <code>{toUaDate(docStart.date)}</code>
              <CopyButton value={toUaDate(docStart.date)} label="дату початку" />
              {docStart.maxMonths > 0 && (
                <span style={{ color: 'var(--muted)', fontSize: '0.85em' }}>
                  {' '}— на {docStart.maxMonths} міс. назад (за вашим профілем
                  найглибше: {docStart.driver})
                </span>
              )}
            </li>
            <li><strong>Дата кінця документів</strong>: <code>{toUaDate(migrationDate)}</code>
              <CopyButton value={toUaDate(migrationDate)} label="дату кінця" /></li>
            <li><strong>Міграційний токен</strong>: вставте значення з блоку вище ↑</li>
          </ul>
        </li>
        <li>Натисніть <strong>«Перевірити підключення»</strong> у обробці —
          має показати ✓
        </li>
        <li>Натисніть «<strong>Вивантажити дані</strong>» у обробці</li>
        <li>Зачекайте на завершення міграції (від 5 хв до 2 годин)</li>
      </ol>

      <div style={{ background: 'var(--badge-amber-bg)', padding: '0.75rem', borderRadius: 6, marginTop: '1rem' }}>
        ⚠️ <strong>Перед запуском обробки</strong> — натисніть «Я готовий, стартую»
        нижче. Сервіс позначить run як <code>READY</code> і чекатиме на дані з 1С/BAS.
      </div>

      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} disabled={isPending}>← Назад</button>
        {alreadyStarted ? (
          // ч.1139: перенесення вже запущено (продовжуємо його) — повторний
          // запуск не потрібен і сервер його відхилив би.
          <button className="primary" onClick={onGoProgress} data-testid="wizard-go-progress">
            перенесення вже запущено — до прогресу →
          </button>
        ) : (
          <button className="primary" onClick={onStart} disabled={isPending}>
            {isPending ? 'Підготовка…' : 'Я готовий, стартую →'}
          </button>
        )}
      </div>
      {startError && (
        <p data-testid="wizard-start-error" style={{ marginTop: '0.5rem', color: 'var(--danger)' }}>
          {startError}
        </p>
      )}
    </fieldset>
  );
}

interface TokenCreatorProps {
  organizationId: string;
}

function TokenCreator({ organizationId }: TokenCreatorProps) {
  const [tokenName, setTokenName] = useState('Wizard migration');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useMutation({
    mutationFn: () => migrationApi.createToken({
      organization_id: organizationId,
      name: tokenName,
      expires_in_days: 30,
    }),
    onSuccess: (data) => {
      if (data.token) {
        setCreatedToken(data.token);
        setCopied(false);
      }
    },
  });

  async function handleCopy() {
    if (!createdToken) return;
    try {
      await navigator.clipboard.writeText(createdToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select + execCommand
    }
  }

  if (createdToken) {
    return (
      <div
        style={{
          background: 'var(--badge-green-bg)',
          border: '1px solid #0a8',
          padding: '1rem',
          borderRadius: 6,
        }}
      >
        <strong>✅ Токен створено</strong>
        <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
          Скопіюйте і вставте у поле «Міграційний токен» у формі 1С/BAS
          обробки. <strong>Зберігається лише зараз</strong> — після
          переходу на інший крок не зможемо показати знову.
        </p>
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            background: 'var(--bg-panel)',
            padding: '0.5rem',
            border: '1px solid #ccc',
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: '0.85em',
            wordBreak: 'break-all',
          }}
        >
          <span style={{ flex: 1 }}>{createdToken}</span>
          <button onClick={handleCopy} className="primary">
            {copied ? '✓ Скопійовано' : '📋 Копіювати'}
          </button>
        </div>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.85em', color: 'var(--muted)' }}>
          Термін дії: 30 днів. Якщо втратите — створіть новий тут.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: 'var(--bg-soft-2)',
        border: '1px solid var(--border)',
        padding: '1rem',
        borderRadius: 6,
      }}
    >
      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9em' }}>
        Назва токена (для вашої довідки):
      </label>
      <input
        type="text"
        value={tokenName}
        onChange={(e) => setTokenName(e.target.value)}
        placeholder="Wizard migration"
        style={{ width: '100%', padding: '0.4rem', marginBottom: '0.5rem' }}
      />
      <button
        className="primary"
        onClick={() => createMutation.mutate()}
        disabled={createMutation.isPending || !tokenName.trim()}
      >
        {createMutation.isPending ? 'Створюю…' : '🔑 Створити токен'}
      </button>
      {createMutation.error !== null && createMutation.error !== undefined && (
        <p style={{ color: 'var(--danger)', marginTop: '0.5rem', fontSize: '0.85em' }}>
          Помилка: {String(createMutation.error)}
        </p>
      )}
    </div>
  );
}

interface Step6Props {
  run: MigrationRun | null;
  onReset: () => void;
  onBulkPost: () => void;
  bulkPostPending: boolean;
  /** ч.1137: скинути перенесення, що зупинилось на прийомі даних. */
  onResetRun: (id: string) => void;
  /** ч.1150: назад до етапу 5 (новий ключ), коли перенесення ще чекає даних. */
  onBackToKey: () => void;
}

// Phase-3.5 (2026-06-01): людиночитані назви 1С/BAS конфігурацій.
// Дзеркало `SourceConfig` у backend schemas/migration_run.py.
const SOURCE_CONFIG_NAMES: Record<string, string> = {
  utp: '1С УТП',
  generic: '1С (інша конфігурація)',
  '1c_bp_ua_2x': '1С БП України 2.x',
  bas_bukh: 'BAS Бухгалтерія',
  ut_ua: '1С УТ / BAS УТ',
};

function Step6Waiting({
  run, onReset, onBulkPost, bulkPostPending, onResetRun, onBackToKey,
}: Step6Props) {
  // ч.1045: карантин контрагентів показує чек-ап (CHK-020), окремого блоку тут немає.

  if (!run) {
    return <p>Завантаження…</p>;
  }

  const isTerminal =
    run.status === 'COMPLETED' ||
    run.status === 'FAILED' ||
    run.status === 'ROLLED_BACK';

  // ч.1139: проводити можна лише після завершення прийому. У стані «прийом
  // даних» кнопка провела б те, що встигло зайти з обірваного імпорту, —
  // сервер у цьому стані теж відмовляє.
  const receiving = run.status === 'IMPORTING';
  const posting = run.status === 'BULK_POSTING';

  // Phase-3.5: badge з detected source після першого POST з 1С обробки.
  const sourceName = run.source_config
    ? (SOURCE_CONFIG_NAMES[run.source_config] || run.source_config)
    : null;

  return (
    <fieldset style={{ padding: '1rem', border: '1px solid var(--border)', borderRadius: 6 }}>
      <legend>Етап 6: Прогрес міграції</legend>

      <div style={{ margin: '1rem 0' }}>
        <strong>Статус:</strong> <code>{run.status}</code>
        {sourceName && (
          <span
            style={{
              marginLeft: '0.75rem',
              padding: '0.15rem 0.5rem',
              background: 'var(--primary-soft)',
              border: '1px solid #06c',
              borderRadius: 4,
              fontSize: '0.85em',
              color: 'var(--primary)',
            }}
            title="Джерело визначено автоматично 1С обробкою з payload"
          >
            📥 {sourceName}
          </span>
        )}
      </div>

      <div style={{ background: 'var(--bg-soft)', borderRadius: 6, height: 24, overflow: 'hidden' }}>
        <div
          style={{
            width: `${run.progress_percent}%`,
            background: run.status === 'FAILED' ? 'var(--danger)' : run.status === 'COMPLETED' ? 'var(--success)' : 'var(--primary)',
            height: '100%',
            transition: 'width 0.5s',
            color: '#fff',
            textAlign: 'center',
            lineHeight: '24px',
          }}
        >
          {run.progress_percent}%
        </div>
      </div>

      {run.progress_message && (
        <p style={{ marginTop: '1rem', color: 'var(--muted)' }}>
          {run.progress_message}
        </p>
      )}

      {/* Block C Порція 2 (2026-06-01): reconciliation report —
          показуємо коли engine відпрацював (після POST з 1С). */}
      {run.reconciliation_report && (
        <ReconciliationReportSection
          runId={run.id}
          report={run.reconciliation_report}
        />
      )}

      {/* Звірка ОСВ (2026-07-19): еталонні обороти з регістра бухгалтерії
          1С vs наші проводки — коли обробка слала секцію osv_1c. */}
      {run.reconciliation_report?.osv && (
        <OsvReconciliationSection osv={run.reconciliation_report.osv} />
      )}

      {/* ч.1040: чек-ап після міграції — зауваження для бухгалтера на момент
          проведення (актуальний стан — на сторінці /migration/checkup). */}
      {run.reconciliation_report?.audit && (
        <CheckupSection audit={run.reconciliation_report.audit} />
      )}

      {/* Block B Phase 3: коли 1С завершила (IMPORTING), показуємо стан bulk-POST.
          Auto-fire спрацьовує одразу при переході у IMPORTING (див. useEffect).
          Кнопка лишається як safety-net для resume case (статус=BULK_POSTING
          з попередньої сесії) — рідкісний edge case. */}
      {(receiving || posting) && (
        <div
          style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'var(--primary-soft)',
            border: '1px solid #06c',
            borderRadius: 6,
          }}
        >
          <strong>
            {receiving
              ? 'Приймаємо дані з обробки 1С. Проведення почнеться, щойно прийом завершиться.'
              : 'Документи проводяться. Після проведення — перевірка залишків.'}
          </strong>
          <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
            Сервіс сам проводить перенесені документи (каса, банк, товари,
            зарплата) і звіряє залишки.
          </p>
          <p style={{ margin: '0.5rem 0', fontSize: '0.85em', color: 'var(--muted)' }}>
            <strong>⏱ Очікуваний час</strong>: від 5 хв (малий бізнес) до 30 хв
            (велике підприємство).
            <br />
            <strong>Не закривайте</strong> вкладку браузера.
          </p>
          {/* Manual fallback button: на випадок коли auto-fire не спрацював
              (resume після reload, мережева помилка mutation, тощо). */}
          {/* У стані «проведення документів» кнопка лишається активною: якщо
              фонове проведення обірвалось, її натискають, щоб продовжити. */}
          <button
            className="primary"
            onClick={onBulkPost}
            disabled={bulkPostPending || receiving}
            title={receiving
              ? 'Проведення почнеться після завершення прийому даних з 1С'
              : 'Уже проведені документи повторно не проводяться'}
            style={{ marginTop: '0.5rem' }}
          >
            {bulkPostPending ? 'проведення триває…' : 'запустити проведення вручну'}
          </button>
        </div>
      )}

      {/* ч.1150: перенесення запущене, але даних ще немає (READY) — ключ не підійшов
          або загубився (власник: «неверный токен, сбросить не могу»). Шлях назад:
          до етапу 5 (новий ключ) або скинути перенесення. */}
      {run.status === 'READY' && (
        <div
          data-testid="wizard-ready-back"
          style={{
            marginTop: '1rem', padding: '0.75rem', borderRadius: 6,
            background: 'var(--bg-soft)', border: '1px solid var(--border)',
          }}
        >
          <strong>Перенесення чекає даних з 1С.</strong>
          <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
            Обробка пише «Невірний токен» або ключ не скопіювався? Поверніться до
            значень для обробки і створіть новий ключ — старий показувався один раз.
            Щоб змінити модель чи дату, скиньте перенесення: дані ще не приймались,
            нічого не втрачається.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="primary" onClick={onBackToKey}>← до значень для обробки</button>
            <button className="danger" onClick={() => onResetRun(run.id)}>скинути перенесення</button>
          </div>
        </div>
      )}

      {/* ч.1137: прийом даних не завершився (обробка 1С упала або її закрили) —
          перенесення можна скинути на крок налаштувань; дані лишаються. */}
      {run.status === 'IMPORTING' && (
        <div
          data-testid="wizard-importing-reset"
          style={{
            marginTop: '1rem', padding: '0.75rem', borderRadius: 6,
            background: 'var(--bg-soft)', border: '1px solid var(--border)',
          }}
        >
          <strong>Обробка 1С зупинилась з помилкою або її закрили?</strong>
          <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
            Повторіть вивантаження в обробці — те, що вже прийнято, оновиться, дублів
            не буде. Щоб змінити налаштування перенесення, скиньте його: прийняті дані
            залишаться в сервісі.
          </p>
          <button className="danger" onClick={() => onResetRun(run.id)}>
            скинути перенесення
          </button>
        </div>
      )}

      {/* ч.1045: людський текст замість сирого JSON (рішення власника). */}
      {run.error_report !== null && (
        <div style={{ color: 'var(--danger)', padding: '0.5rem 0.75rem', background: 'var(--badge-red-bg)', marginTop: '1rem', borderRadius: 6 }}>
          <strong>Помилка проведення:</strong>{' '}
          {errorReportText(run.error_report)}
        </div>
      )}

      {run.validation_report !== null && (
        <MigrationReportSummary report={run.validation_report} />
      )}

      <RunHelpRequest run={run} />

      {run.status === 'COMPLETED' && (
        <div
          style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'var(--badge-green-bg)',
            border: '1px solid #0a8',
            borderRadius: 6,
          }}
        >
          <strong>🎉 Міграція завершена успішно!</strong>
          <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }}>
            Ваші дані доступні у сервісі. Можете починати працювати.
          </p>
          {/* ч.1161: чек-ап рахується за поточним станом бази — посилання завжди,
              навіть коли у цьому прогоні блоку чек-апу немає (нічого не проводилось). */}
          {!run.reconciliation_report?.audit && (
            <p style={{ margin: '0.5rem 0', fontSize: '0.9em' }} data-testid="wizard-checkup-link">
              Зауваження для бухгалтера за поточним станом бази —{' '}
              <Link to={`/migration/checkup?org=${run.organization_id}`}>відкрити чек-ап після міграції →</Link>
              {' '}(є і в меню «Головна → Міграція»).
            </p>
          )}
          {run.reverse_export_available_until && (
            <p style={{ margin: '0.5rem 0', fontSize: '0.85em', color: 'var(--muted)' }}>
              💡 <strong>30 днів зворотньої міграції</strong>: до{' '}
              <code>{new Date(run.reverse_export_available_until).toLocaleDateString('uk')}</code>{' '}
              ви можете повернутись у 1С без обмежень.
            </p>
          )}
        </div>
      )}

      {isTerminal && (
        <div style={{ marginTop: '1.5rem' }}>
          <button onClick={onReset}>почати нове перенесення</button>
        </div>
      )}
    </fieldset>
  );
}

// ============================================================
// Block C Порція 2 (2026-06-01): Reconciliation report UI
// ============================================================

// Кольори/іконки/заголовки по джерелу.
const RECON_SOURCE_META: Record<ReconciliationSource, {
  color: string;
  bgColor: string;
  icon: string;
  title: string;
}> = {
  production_calendar: {
    color: 'var(--primary)',
    bgColor: 'var(--primary-soft)',
    icon: '📅',
    title: 'Виробничий календар',
  },
  accounting_constants: {
    color: 'var(--badge-amber-fg)',
    bgColor: 'var(--badge-amber-bg)',
    icon: '⚖️',
    title: 'Бухгалтерські константи',
  },
  period_date: {
    color: 'var(--danger)',
    bgColor: 'var(--badge-red-bg)',
    icon: '⏱',
    title: 'Дата періоду (recalc заднім числом)',
  },
};

interface ReconciliationReportSectionProps {
  runId: string;
  report: ReconciliationReport;
}

function ReconciliationReportSection({ runId, report }: ReconciliationReportSectionProps) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(true);

  // Block C Порція 3 (2026-06-01): list уже створених overrides
  // щоб не пропонувати «Прийняти 1С» повторно.
  const overridesQuery = useQuery({
    queryKey: ['migration-overrides', runId],
    queryFn: () => migrationRuns.listOverrides(runId),
  });

  const overrideAcceptedKey = (f: ReconciliationFinding): string =>
    `${f.target_table ?? ''}|${f.document_external_id ?? ''}|${f.field}`;

  const acceptedKeys = new Set(
    (overridesQuery.data ?? []).map((o) =>
      `${o.target_table}|${o.target_id ?? ''}|${o.field_name}`,
    ),
  );

  const createOverride = useMutation({
    mutationFn: (f: ReconciliationFinding) =>
      migrationRuns.createOverride(runId, {
        target_table: f.target_table ?? 'doc.vacation_calculations',
        target_id: null,  // TODO: resolve external_id → UUID
        field_name: f.field,
        expected_value: f.our_value,
        override_value: f.their_value,
        reason: `Прийнято 1С/BAS значення з reconciliation: ${f.context}`,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['migration-overrides', runId] });
    },
  });

  const { total_findings, estimated_impact_uah, blocking_findings } =
    report.summary;

  if (total_findings === 0) {
    return (
      <div
        style={{
          marginTop: '1.5rem',
          padding: '0.75rem 1rem',
          background: 'var(--badge-green-bg)',
          border: '1px solid #0a8',
          borderRadius: 6,
          fontSize: '0.9em',
        }}
      >
        ✅ <strong>Звіт reconciliation</strong>: розбіжностей з 1С/BAS не
        виявлено. Можна спокійно запускати bulk-POST.
      </div>
    );
  }

  // Групуємо findings по source
  const grouped = report.findings.reduce<Record<string, ReconciliationFinding[]>>(
    (acc, f) => {
      const key = f.source;
      if (!acc[key]) acc[key] = [];
      acc[key].push(f);
      return acc;
    },
    {},
  );

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: blocking_findings > 0 ? 'var(--badge-amber-bg)' : 'var(--bg-soft-2)',
        border: `1px solid ${blocking_findings > 0 ? 'var(--badge-amber-bd)' : 'var(--border)'}`,
        borderRadius: 6,
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div>
          <strong>
            {blocking_findings > 0 ? '⚠️' : 'ℹ️'} Звіт reconciliation:{' '}
            знайдено <strong>{total_findings}</strong> розбіжностей
          </strong>
          <div style={{ fontSize: '0.85em', color: 'var(--muted)', marginTop: '0.25rem' }}>
            Орієнтовний вплив: <strong>{estimated_impact_uah} грн</strong>
            {blocking_findings > 0 && (
              <>
                {' · '}
                <span style={{ color: 'var(--danger)' }}>
                  ⚠️ {blocking_findings} серйозн{blocking_findings === 1 ? 'е' : 'их'}{' '}
                  (вплив &gt;100 грн)
                </span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={() => setExpanded((e) => !e)}
          style={{ background: 'transparent', border: '1px solid #ccc', cursor: 'pointer' }}
        >
          {expanded ? 'Згорнути' : 'Розгорнути деталі'}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: '1rem' }}>
          <p style={{ fontSize: '0.9em', color: 'var(--muted)', marginBottom: '0.75rem' }}>
            Закон 5: мігровані числа з 1С/BAS не змінюються. Цей звіт
            інформує про <strong>можливі різниці</strong> у нових
            нарахуваннях, які наш сервіс зробить після міграції.
          </p>
          {(Object.entries(grouped) as [ReconciliationSource, ReconciliationFinding[]][]).map(
            ([source, findings]) => {
              const meta = RECON_SOURCE_META[source];
              return (
                <details
                  key={source}
                  open
                  style={{
                    marginBottom: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    background: meta.bgColor,
                    border: `1px solid ${meta.color}`,
                    borderRadius: 4,
                  }}
                >
                  <summary
                    style={{
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      color: meta.color,
                    }}
                  >
                    {meta.icon} {meta.title} ({findings.length})
                  </summary>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85em' }}>
                    {findings.slice(0, 5).map((f, i) => (
                      <ReconciliationFindingRow
                        key={i}
                        finding={f}
                        isAccepted={acceptedKeys.has(overrideAcceptedKey(f))}
                        onAccept={() => createOverride.mutate(f)}
                        isPending={createOverride.isPending}
                      />
                    ))}
                    {findings.length > 5 && (
                      <p style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
                        … і ще {findings.length - 5} аналогічних знайдень
                      </p>
                    )}
                  </div>
                </details>
              );
            },
          )}
        </div>
      )}
    </div>
  );
}

interface ReconciliationFindingRowProps {
  finding: ReconciliationFinding;
  isAccepted: boolean;
  onAccept: () => void;
  isPending: boolean;
}

function ReconciliationFindingRow({
  finding, isAccepted, onAccept, isPending,
}: ReconciliationFindingRowProps) {
  return (
    <div
      style={{
        padding: '0.5rem',
        background: isAccepted ? 'var(--badge-green-bg)' : 'var(--bg-panel)',
        borderRadius: 3,
        marginBottom: '0.25rem',
        border: isAccepted ? '1px solid #0a8' : '1px solid #eee',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <code style={{ fontSize: '0.85em' }}>{finding.field}</code>
        <span style={{ fontSize: '0.85em', color: 'var(--muted)' }}>
          {finding.context}
        </span>
      </div>
      <div style={{ marginTop: '0.25rem', fontSize: '0.85em' }}>
        Наше: <code>{String(finding.our_value)}</code>
        {' · '}
        1С/BAS: <code>{String(finding.their_value)}</code>
        {' · '}
        Вплив: <strong>{finding.difference_amount} грн</strong>
      </div>
      <div style={{ marginTop: '0.25rem', fontSize: '0.85em', color: 'var(--muted)' }}>
        💡 {finding.recommendation}
      </div>
      {/* Block C Порція 3: «Прийняти 1С» кнопка. Невидима для empty target. */}
      {finding.target_table && (
        <div style={{ marginTop: '0.5rem' }}>
          {isAccepted ? (
            <span style={{ color: 'var(--success)', fontSize: '0.85em' }}>
              ✅ 1С/BAS значення прийнято (recalc використає це)
            </span>
          ) : (
            <button
              type="button"
              onClick={onAccept}
              disabled={isPending}
              title="Створити override: при recalc цього документа використати 1С/BAS значення замість обчисленого"
              style={{
                fontSize: '0.85em',
                padding: '0.25rem 0.5rem',
                background: 'var(--bg-panel)',
                border: '1px solid #06c',
                borderRadius: 3,
                cursor: isPending ? 'wait' : 'pointer',
                color: 'var(--primary)',
              }}
            >
              {isPending ? '⏳ Зберігаю…' : '✓ Прийняти 1С/BAS'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Звірка ОСВ з 1С (2026-07-19): еталонні обороти Дт×Кт з регістра
// бухгалтерії 1С (секція osv_1c обробки) проти наших acc.entries.
// ============================================================

function fmtUah(s: string): string {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString('uk-UA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const SECTION_LABELS: Record<string, string> = {
  bank: 'банк', cash: 'каса', payrolls: 'нарахування ЗП', hr_orders: 'кадрові накази',
  production: 'виробництво', goods_sales: 'реалізації', goods_receipts: 'надходження',
  sales_cycle: 'цикл продажів (повернення, послуги, ПН, взаємозаліки)',
  payroll_payments: 'виплати ЗП', manual_operations: 'довідки', customs_declarations: 'ВМД',
  vacation_start_balance: 'стартові залишки відпусток',
};
const CHECK_LABELS: Record<string, string> = {
  cash_bank_balance: 'залишки каси/банку', payroll_balance: 'баланс зарплати',
  document_counts: 'кількість документів', sum_reconciliation: 'звірка сум',
  orphan_detection: 'посилання без батьків', tenant_isolation_final: 'ізоляція даних',
};

/** ч.1438 (рішення власника): перенесення пішло не так — звернення одразу до спеціаліста. */
export function RunHelpRequest({ run }: { run: MigrationRun }) {
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const q = useQuery({
    queryKey: ['migration-run-help', run.id, run.status, run.updated_at],
    queryFn: () => migrationRuns.helpState(run.id),
    // «проведення затягнулось» сервер рахує за часом — перевіряємо зрідка й тоді, коли прогін стоїть
    refetchInterval: 5 * 60_000,
  });
  const send = useMutation({
    mutationFn: () => migrationRuns.requestHelp(run.id, comment.trim()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['migration-run-help', run.id] }),
  });
  const st = q.data;
  if (!st || (st.problems.length === 0 && !st.case && !send.data)) return null;
  const caseId = send.data?.case ?? st.case;
  return (
    <div data-testid="migration-help" className="mc__panel mc__panel--warn" style={{ marginTop: '1rem' }}>
      <strong>З перенесенням щось не так</strong>
      {st.problems.length > 0 && (
        <ul style={{ margin: '0.4rem 0', paddingLeft: '1.2rem' }}>
          {st.problems.map((p) => <li key={p.code}>{p.text}</li>)}
        </ul>
      )}
      {caseId ? (
        <p style={{ margin: '0.4rem 0 0' }} role="status">
          Звернення <strong>{caseId}</strong> передано розробнику. {st.response_time} Відповідь
          зʼявиться у «Підтримці» — значок «?» праворуч унизу.
        </p>
      ) : (
        <>
          <div className="field" style={{ margin: '0.5rem 0' }}>
            <label htmlFor="migration-help-comment">Що ви бачите (необовʼязково)</label>
            <textarea id="migration-help-comment" rows={3} maxLength={2000} value={comment}
              onChange={(e) => setComment(e.target.value)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button type="button" className="primary" disabled={send.isPending} onClick={() => send.mutate()}>
              {send.isPending ? 'надсилаємо…' : 'запросити допомогу підтримки'}
            </button>
            <span style={{ fontSize: '0.85em' }}>{st.response_time}</span>
          </div>
          {send.isError && <div role="alert" style={{ marginTop: '0.4rem' }}>Не вдалося надіслати — спробуйте ще раз.</div>}
        </>
      )}
    </div>
  );
}

/** Текст помилки замість сирого JSON error_report. */
export function errorReportText(err: Record<string, unknown>): string {
  const section = typeof err.section === 'string' ? err.section : '';
  const detail = typeof err.detail === 'string' ? err.detail : '';
  if (section || detail) return `${section ? `секція «${section}»: ` : ''}${detail || 'деталі у журналі міграції'}`;
  return 'проведення зупинилось — зверніться до підтримки (деталі у журналі міграції)';
}

/** ч.1258: документ, який автопроведення взялось провести і не змогло, — і причина. */
export interface NotPostedItem {
  section?: string | null;
  document_type?: string | null;
  document_id?: string | null;
  number?: string | null;
  date?: string | null;
  reason: string;
}

const NOT_POSTED_SHOWN = 10;

/** «Звіти виробництва за зміну № 12 від 15.01.2025»; без документа — розділ проведення. */
export function notPostedLabel(it: NotPostedItem): string {
  if (!it.document_type) return it.section ? `розділ «${SECTION_LABELS[it.section] ?? it.section}»` : 'документ';
  const day = it.date ? new Date(`${it.date}T00:00:00`).toLocaleDateString('uk-UA') : '';
  return [docTableName(it.document_type), it.number ? `№ ${it.number}` : '', day ? `від ${day}` : '']
    .filter(Boolean).join(' ');
}

/** ч.1045: «Звіт міграції» людською мовою: що проведено + системні перевірки. */
export function MigrationReportSummary({ report }: { report: Record<string, unknown> }) {
  const sections = (report.sections ?? {}) as Record<string, number>;
  const np = (report.not_posted ?? null) as { total?: number; items?: NotPostedItem[] } | null;
  const npItems = Array.isArray(np?.items) ? np.items : [];
  const npTotal = typeof np?.total === 'number' ? np.total : npItems.length;
  const npShown = npItems.slice(0, NOT_POSTED_SHOWN);
  // залишок — від того, що справді показано: сервер може віддати менше рядків, ніж total
  const npRest = npTotal - npShown.length;
  // ч.1261: примітки проведення — що сервіс зробив сам і що бухгалтеру варто знати
  const notes = (Array.isArray(report.notes) ? report.notes : [])
    .filter((n): n is string => typeof n === 'string' && n.trim() !== '');
  const total = typeof report.total_posted === 'number' ? report.total_posted : null;
  const checks = (Array.isArray(report.sentinel_checks) ? report.sentinel_checks : []) as
    { check: string; status: string; issues?: unknown[] }[];
  const posted = Object.entries(sections).filter(([, n]) => Number(n) > 0);
  const tone = (st: string) => (st === 'OK' ? 'green' : st === 'WARN' ? 'amber' : 'red');
  return (
    <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-panel)' }}>
      <strong>Звіт міграції</strong>
      {total !== null && (
        <div style={{ marginTop: '0.35rem' }}>
          Проведено документів: <strong>{total.toLocaleString('uk-UA')}</strong>
          {posted.length > 0 && (
            <span style={{ color: 'var(--muted)' }}>
              {' '}— {posted.map(([k, n]) => `${SECTION_LABELS[k] ?? k} ${Number(n).toLocaleString('uk-UA')}`).join(', ')}
            </span>
          )}
        </div>
      )}
      {checks.length > 0 && (
        <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem' }}>
          {checks.map((c) => (
            <li key={c.check}>
              <span style={{
                color: `var(--badge-${tone(c.status)}-fg)`, background: `var(--badge-${tone(c.status)}-bg)`,
                border: `1px solid var(--badge-${tone(c.status)}-bd)`, borderRadius: 4,
                padding: '0 0.35rem', fontSize: '0.75rem', fontWeight: 600, marginRight: '0.4rem',
              }}>
                {c.status === 'OK' ? 'ок' : c.status === 'WARN' ? 'увага' : 'помилка'}
              </span>
              {CHECK_LABELS[c.check] ?? c.check}
              {(c.issues?.length ?? 0) > 0 && (
                <span style={{ color: 'var(--muted)' }}> — зауважень: {c.issues!.length}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {notes.length > 0 && (
        <ul data-testid="wizard-report-notes" style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem' }}>
          {notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
      {npTotal > 0 && (
        <div data-testid="wizard-not-posted" style={{
          marginTop: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: 6,
          background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)',
        }}>
          <strong>Не проведено: {npTotal.toLocaleString('uk-UA')}</strong>
          <span style={{ color: 'var(--muted)' }}>
            {' '}— ці документи сервіс узявся провести і не зміг. Усуньте причину і проведіть
            документ або запустіть перенесення ще раз.
          </span>
          <ul style={{ margin: '0.4rem 0 0.25rem', paddingLeft: '1.1rem' }}>
            {npShown.map((it, i) => {
              const route = it.document_type && it.document_id ? DOC_ROUTES[it.document_type] : undefined;
              const label = notPostedLabel(it);
              return (
                <li key={it.document_id ?? `${it.section}-${i}`}>
                  {route ? <Link to={route(it.document_id!)}>{label}</Link> : label}
                  {' — '}{it.reason}
                </li>
              );
            })}
            {npRest > 0 && (
              <li style={{ color: 'var(--muted)' }}>… і ще {npRest.toLocaleString('uk-UA')}</li>
            )}
          </ul>
          <Link to="/monitor/posting?migrated=1&period=all">усі непроведені документи — у моніторі проведень →</Link>
        </div>
      )}
    </div>
  );
}

function CheckupSection({ audit }: { audit: CheckupReport }) {
  const s = audit.summary;
  const tone = s.error ? 'red' : s.warn ? 'amber' : 'green';
  const top = sortFindings(audit.findings).slice(0, 6);
  return (
    <div
      style={{
        marginTop: '1.5rem', padding: '1rem',
        background: `var(--badge-${tone}-bg)`,
        border: `1px solid var(--badge-${tone}-bd)`,
        borderRadius: 6,
      }}
    >
      <strong>Чек-ап після міграції:</strong> {summaryLine(s)}
      <span style={{ color: 'var(--muted)', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
        (перевірок: {s.rules_run})
      </span>
      {top.length > 0 && (
        <ul style={{ margin: '0.5rem 0 0.25rem' }}>
          {top.map((f) => (
            <li key={f.code}>
              {f.title} — <strong>{f.count.toLocaleString('uk-UA')}</strong>
              <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                {' '}({SEVERITY_LABEL[f.severity]})
              </span>
            </li>
          ))}
          {audit.findings.length > top.length && (
            <li style={{ color: 'var(--muted)' }}>… і ще {audit.findings.length - top.length}</li>
          )}
        </ul>
      )}
      <Link to="/migration/checkup">відкрити чек-ап →</Link>
    </div>
  );
}

function OsvReconciliationSection({ osv }: { osv: OsvReconciliation }) {
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const [showPairs, setShowPairs] = useState(false);

  if (osv.error) {
    return (
      <div
        style={{
          marginTop: '1.5rem', padding: '0.75rem 1rem',
          background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)', borderRadius: 6,
        }}
      >
        ⚠️ <strong>Звірка ОСВ</strong>: {osv.error}
      </div>
    );
  }
  const s = osv.summary;
  if (!s) return null;

  const accounts = osv.accounts ?? [];
  const badAccounts = accounts.filter((a) => !a.ok);
  const shownAccounts = showAllAccounts ? accounts : badAccounts;
  const pairs = osv.pairs ?? [];
  const allOk = s.accounts_diff === 0;

  const th: CSSProperties = {
    textAlign: 'right', padding: '0.3rem 0.5rem',
    borderBottom: '1px solid #ccc', whiteSpace: 'nowrap',
  };
  const td: CSSProperties = {
    textAlign: 'right', padding: '0.25rem 0.5rem',
    borderBottom: '1px solid #eee', whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  };
  const diffStyle = (v: string): CSSProperties =>
    Math.abs(Number(v)) >= 0.01
      ? { ...td, color: 'var(--danger)', fontWeight: 600 }
      : { ...td, color: 'var(--success)' };

  return (
    <div
      style={{
        marginTop: '1.5rem', padding: '1rem',
        background: allOk ? 'var(--badge-green-bg)' : 'var(--badge-amber-bg)',
        border: `1px solid ${allOk ? 'var(--success)' : 'var(--badge-amber-bd)'}`,
        borderRadius: 6,
      }}
    >
      <strong>
        {allOk ? '✅' : '⚠️'} Звірка ОСВ з 1С
        {osv.period && (
          <span style={{ fontWeight: 400 }}>
            {' '}({osv.period.from} — {osv.period.to})
          </span>
        )}
      </strong>
      <div style={{ fontSize: '0.85em', color: 'var(--muted)', marginTop: '0.25rem' }}>
        Обороти 1С: <strong>{fmtUah(s.total_1c)}</strong> грн ·{' '}
        наші: <strong>{fmtUah(s.total_ours)}</strong> грн ·{' '}
        різниця: <strong>{fmtUah(s.total_diff)}</strong> грн ·{' '}
        рахунків збігається <strong>{s.accounts_ok}</strong> з{' '}
        <strong>{s.accounts_total}</strong>
      </div>

      {allOk ? (
        <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
          Усі обороти по рахунках збігаються з регістром бухгалтерії 1С
          копійка в копійку.
        </div>
      ) : (
        <>
          <div style={{ marginTop: '0.75rem', overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.82em', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Рахунок</th>
                  <th style={th}>Дт 1С</th>
                  <th style={th}>Дт наші</th>
                  <th style={th}>Δ Дт</th>
                  <th style={th}>Кт 1С</th>
                  <th style={th}>Кт наші</th>
                  <th style={th}>Δ Кт</th>
                </tr>
              </thead>
              <tbody>
                {shownAccounts.map((a) => (
                  <tr key={a.code}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{a.code}</td>
                    <td style={td}>{fmtUah(a.dt_1c)}</td>
                    <td style={td}>{fmtUah(a.dt_ours)}</td>
                    <td style={diffStyle(a.diff_dt)}>{fmtUah(a.diff_dt)}</td>
                    <td style={td}>{fmtUah(a.kt_1c)}</td>
                    <td style={td}>{fmtUah(a.kt_ours)}</td>
                    <td style={diffStyle(a.diff_kt)}>{fmtUah(a.diff_kt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setShowAllAccounts((v) => !v)}
              style={{ background: 'transparent', border: '1px solid #ccc', cursor: 'pointer', fontSize: '0.85em' }}
            >
              {showAllAccounts
                ? 'лише розбіжності'
                : `показати всі рахунки (${accounts.length})`}
            </button>
            {pairs.length > 0 && (
              <button
                onClick={() => setShowPairs((v) => !v)}
                style={{ background: 'transparent', border: '1px solid #ccc', cursor: 'pointer', fontSize: '0.85em' }}
              >
                {showPairs
                  ? 'сховати кореспонденції'
                  : `кореспонденції з розбіжністю (${pairs.length}${(osv.pairs_truncated ?? 0) > 0 ? '+' : ''})`}
              </button>
            )}
          </div>
          {showPairs && (
            <div style={{ marginTop: '0.5rem', overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '0.82em' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'left' }}>Дт → Кт</th>
                    <th style={th}>1С</th>
                    <th style={th}>наші</th>
                    <th style={th}>Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {pairs.map((p) => (
                    <tr key={`${p.dt}|${p.kt}`}>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>
                        {p.dt} → {p.kt}
                      </td>
                      <td style={td}>{fmtUah(p.amount_1c)}</td>
                      <td style={td}>{fmtUah(p.amount_ours)}</td>
                      <td style={diffStyle(p.diff)}>{fmtUah(p.diff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(osv.pairs_truncated ?? 0) > 0 && (
                <div style={{ fontSize: '0.8em', color: 'var(--muted)', marginTop: '0.25rem' }}>
                  … та ще {osv.pairs_truncated} кореспонденцій (показано топ-300 за розбіжністю)
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
